import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import PropTypes from "prop-types";
import { Heading } from "@chakra-ui/react";
import LinePlot from "./LinePlot";
import LinePlot2 from "./LinePlot2";
import AudioPlayer from "./AudioPlayer";
import { useColorMode } from "@chakra-ui/react";
import "./ScatterPlot.css";
import { ENDPOINT } from "views/Dashboard/Dashboard/api";


const ScatterPlot = ({
  title = "",
  feature = "",
  data = [],
  events = [],
  weights = [],
  avgk = 3,
  daysMax = 150,
  width = 1200,
  height = 400,
  marginTop = 20,
  marginRight = 20,
  marginBottom = 50,
  marginLeft = 70,
}) => {
  const { colorMode, toggleColorMode } = useColorMode();

  const svgRef = useRef();
  const gxRef = useRef();
  const gyRef = useRef();
  const [selectedSeries, setSelectedSeries] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState("");
  const [selectedDay, setSelectedDay] = useState(-1);
  const [cnt, setCnt] = useState(0);
  const [audioUrl, setAudioUrl] = useState("");
  const [imgUrl, setImgUrl] = useState("");

  // const ENDPOINT = "https://unisono-bck.zana.ai/zf";
  // const ENDPOINT = "http://127.0.0.1:8000";

  const getMovingAverage = (array = [], k) => {
    if (!Array.isArray(array) || array.length === 0) {
      return [];
    }

    const minDay = array[0][0];
    const maxDay = array[array.length - 1][0];

    const fullArray = Array.from({ length: maxDay - minDay + 1 }, (_, i) => [
      minDay + i,
      null,
    ]);
    array.forEach(([day, value]) => {
      fullArray[day - minDay][1] = value;
    });

    const result = [];
    for (let i = 0; i < fullArray.length; i++) {
      const windowStart = Math.max(0, i - k / 2 + 1);
      const windowEnd = i + k / 2 + 1;
      const windowValues = fullArray
        .slice(windowStart, windowEnd)
        .map((entry) => entry[1])
        .filter((value) => value !== null);

      const meanValue =
        windowValues.length > 0
          ? windowValues.reduce((acc, val) => acc + val, 0) /
            windowValues.length
          : null;

      if (meanValue != null) {
        result.push([fullArray[i][0], meanValue]);
      }
    }

    return result;
  };

  // Prepare data for plot
  const yExtent = [0, d3.max(data.map((d) => d[1]))];
  const xExtent = [0, daysMax];

  // Define scales
  const xScale = d3
    .scaleLinear()
    .domain(xExtent)
    .range([marginLeft, width - marginRight]);
  const yScale = d3
    .scaleLinear()
    .domain(yExtent)
    .range([height - marginBottom, marginTop]);
  const avgData = getMovingAverage(data, avgk);

  useEffect(() => {
    const xAxis = d3.axisBottom(xScale).ticks(Math.min(daysMax, 10));
    const yAxis = d3.axisLeft(yScale).ticks(6);

    d3.select(gxRef.current)
      .attr("transform", `translate(0, ${height - marginBottom})`)
      .call(xAxis);

    // console.log("X", d3.select(gxRef.current).node());

    d3.select(gyRef.current)
      .attr("transform", `translate(${marginLeft}, 0)`)
      .call(yAxis);

    // console.log("Y", d3.select(gyRef.current).node());
    d3.select(gxRef.current)
      .selectAll("text")
      // .style("fill", "black")
      .style("font-size", "20px")
      .style("text-anchor", "middle");

    d3.select(gyRef.current)
      .selectAll("text")
      // .style("fill", "black")
      .style("font-size", "20px")
      .style("text-anchor", "end");

    // d3.select(gxRef.current).selectAll("line")
    //     .style("stroke", "black");

    // d3.select(gyRef.current).selectAll("line")
    //     .style("stroke", "black");
  }, [xScale, yScale, height, marginBottom, marginLeft]);

  useEffect(() => {
    const svg = d3.select(svgRef.current);

    // Clear previous data points, lines, events, etc.
    svg.selectAll(".data-line").remove();
    svg.selectAll(".data-point").remove();
    svg.selectAll(".avg-point").remove();
    svg.selectAll(".weight-line").remove();
    svg.selectAll(".event-line").remove();

    // Draw weights
    svg
      .selectAll(".weight-line")
      .data(weights)
      .enter()
      .append("line")
      .attr("class", "weight-line")
      .attr("x1", (d) => xScale(d[0]))
      .attr("x2", (d) => xScale(d[0]))
      .attr("y1", yScale.range()[0])
      .attr("y2", yScale.range()[1])
      .attr("stroke", "#FBB6CE") //pink
      .attr("stroke-width", 2);

    // Draw data points and lines
    svg
      .selectAll(".data-line")
      .data(data)
      .enter()
      .append("line")
      .attr("class", "data-line")
      .attr("x1", (d) => xScale(d[0]))
      .attr("x2", (d) => xScale(d[0]))
      .attr("y1", yScale.range()[0])
      .attr("y2", (d) => yScale(d[1]))
      .attr("stroke", colorMode === "dark" ? "#6a6a6aff" : "#334") //dark gray
      .attr("stroke-width", 2)
      .style("cursor", "pointer")
      .on("click", (_, d) => onDayClick(d[0]));

    // Draw events
    svg
      .selectAll(".event-line")
      .data(events[0])
      .enter()
      .append("line")
      .attr("class", "event-line")
      .attr("x1", (d) => xScale(d))
      .attr("x2", (d) => xScale(d))
      .attr("y1", yScale.range()[0])
      .attr("y2", yScale.range()[1])
      .attr("stroke", "#ED8936") //orange
      .attr("stroke-width", 2);
    console.log(events);

    // Draw circles for data points
    svg
      .selectAll(".data-point")
      .data(data)
      .enter()
      .append("circle")
      .attr("class", "data-point")
      .attr("cx", (d) => xScale(d[0]))
      .attr("cy", (d) => yScale(d[1]))
      .attr("r", 4)
      .attr("fill", "#2b6cb0") //blue
      .attr("stroke", "#2b6cb0")
      .attr("stroke-width", 1.5)
      .style("cursor", "pointer")
      .on("click", function (event, d) {
        // console.log('Full data point clicked:', d);
        if (Array.isArray(d) && d.length > 0) {
          onDayClick(d[0]);
        } else {
          console.error("Data point does not have a valid format:", d);
        }
      });

    // Draw moving average points
    svg
      .selectAll(".avg-point")
      .data(avgData)
      .enter()
      .append("circle")
      .attr("class", "avg-point")
      .attr("cx", (d) => xScale(d[0]))
      .attr("cy", (d) => yScale(d[1]))
      .attr("r", 3)
      .attr("fill", "#E53E3E")
      .attr("stroke", "#E53E3E") //red
      .attr("stroke-width", 1.5);
  }, [data, events, avgk, daysMax, selectedSeries]);

  // Function to handle data fetch when a day is clicked
  const onDayClick = async (day) => {
    try {
      // console.log('Day clicked:', day);

      if (typeof day === "undefined" || day === null) {
        console.error("Day is undefined or null, unable to proceed.");
        return;
      }

      const patientId = title.split(": ")[1];
      if (!patientId) {
        console.error("Patient ID is not correctly formatted in the title.");
        return;
      }

      const endpoint = `${ENDPOINT}/feature-series?pid=${patientId}&feature=${feature}&d=${day}`;
      // console.log("Fetching data from endpoint:", endpoint);

      // Fetch data from the API
      const fetchedData = await fetch(endpoint).then((response) =>
        response.json()
      );
      // console.log("Fetched data result:", fetchedData);

      // Update state with fetched data
      if (
        fetchedData.data &&
        fetchedData.data.series &&
        fetchedData.data.fileId
      ) {
        setSelectedSeries(fetchedData.data.series);
        setSelectedFileId(fetchedData.data.fileId);
        setSelectedDay(day);
        setCnt(cnt + 1);
        setAudioUrl(`${ENDPOINT}/audio?fileId=${fetchedData.data.fileId}`);
        setImgUrl(
          `${ENDPOINT}/spec-image?fileId=${fetchedData.data.fileId}&${cnt}`
        );
      } else {
        console.error("Unexpected result format:", fetchedData);
      }
    } catch (error) {
      console.error("An error occurred while fetching data:", error);
    }
  };

  return (
    <div className="plot-container">
      <Heading my={6} as="h4" size="md">
        {title}
      </Heading>
      <div>
        <svg ref={svgRef} width={width} height={height} version="1.1">
          <g
            ref={gxRef}
            className="x-axis"
            transform={`translate(0, ${height - marginBottom})`}
          />
          <g
            ref={gyRef}
            className="y-axis"
            transform={`translate(${marginLeft}, 0)`}
          />
        </svg>
      </div>

      {/* Conditional rendering for LinePlot and AudioPlayer */}
      {selectedSeries.length > 0 && (
        <LinePlot2
          title={`${feature}; Day: ${selectedDay}`}
          data={selectedSeries}
          width={width}
          height={250}
        />
      )}
      {selectedDay >= 0 && (
        <div className="player-container">
          <AudioPlayer
            title={title}
            artist={selectedFileId}
            src={audioUrl}
            specUrl={imgUrl}
          />
        </div>
      )}
      <hr className="plot-container" />
    </div>
  );
};

ScatterPlot.propTypes = {
  title: PropTypes.string,
  feature: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.array),
  events: PropTypes.arrayOf(PropTypes.number),
  weights: PropTypes.arrayOf(PropTypes.array),
  avgk: PropTypes.number,
  daysMax: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  marginTop: PropTypes.number,
  marginRight: PropTypes.number,
  marginBottom: PropTypes.number,
  marginLeft: PropTypes.number,
};

export default ScatterPlot;
